import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { PhonicsSound, useGetPhonicsSoundsQuery } from '@/api/types';
import { Lines } from '@/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { findSoundsInString } from '@/utils/sounds';
import { SymbolType, CdnObjectType } from '@/types';
import { CanvasTrace, AudioPlayer } from '@/components';
import { getSoundExtraData } from '@/utils/soundsData';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Toolbar } from '../components/Toolbar';
import { WhitePanel } from '../components/WhitePanel';
import { Underlines } from '../components/Underlines';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { getRandomEntries } from '@/utils/array';
import { LETTERS_WITH_DESCENDERS } from '@/utils/letters';

interface TraceReviewSoundProps {
  sound: string;
}

interface TraceReviewSound {
  asBitmap: () => string | undefined;
  asVector: () => Lines | undefined;
}

const MAX_SOUNDS = 6;

const TraceReviewSound = forwardRef<TraceReviewSound, TraceReviewSoundProps>(
  ({ sound }, ref) => {
    const playerRef = useRef<AudioPlayer>(null);
    const traceRef = useRef<CanvasTrace>(null);

    const { sendAlert } = useUi();

    useImperativeHandle(ref, () => ({
      asBitmap: () => traceRef.current?.asBitmap(),
      asVector: () => traceRef.current?.asVector(),
    }));

    const mediaUrl = useAssetCdn(sound, SymbolType.Sound, CdnObjectType.Audio);

    const [playerReady, setPlayerReady] = useState(false);

    const [, setScore] = useState<number>(0);

    const [soundObject, setSoundObject] = useState<PhonicsSound | undefined>(
      undefined,
    );

    const { data: soundsData } = useGetPhonicsSoundsQuery();

    const { arrows: arrowsPath } = useMemo(() => {
      if (!soundObject) {
        return {
          arrows: undefined,
          lines: undefined,
          lineYOffset: 0,
        };
      }

      return getSoundExtraData(soundObject.name as any);
    }, [soundObject]);

    useEffect(() => {
      if (!soundsData) {
        return;
      }

      setSoundObject(soundsData.getPhonicsSounds.find((s) => s.name === sound));
    }, [soundsData, sound]);

    return (
      <>
        <Toolbar
          onPlay={() => {
            playerRef.current?.play();
          }}
          playDisabled={!playerReady}
          onRefresh={() => traceRef.current?.reset()}
        />

        <WhitePanel mode="basic" kind="fill" color="paper">
          {soundObject ? (
            <div className="relative w-72 mx-auto">
              <CanvasTrace
                ref={traceRef}
                className="w-72 h-72 mx-auto bg-blue-50"
                drawWidth={288}
                drawHeight={288}
                traceBasis={soundObject.referencePath || undefined}
                showBasis={false}
                traceGuide={soundObject.guidePath || undefined}
                showGuide={false}
                additionalSuffixPaths={[
                  ...(arrowsPath && arrowsPath !== ''
                    ? [
                        {
                          path: arrowsPath,
                          strokeWidth: 0,
                        },
                      ]
                    : []),
                ]}
                showScore={false}
                seed="topRight"
                onScoreChange={setScore}
                includeBgOnLines={false}
              />

              <Underlines
                length={1}
                wordSoundsLength={1}
                letterWidthClass="w-[90%]"
                positionOverrideClass={
                  LETTERS_WITH_DESCENDERS.includes(sound) ? 'top-48' : undefined
                }
              />
            </div>
          ) : (
            <p>Unable to build trace object</p>
          )}
        </WhitePanel>

        <AudioPlayer
          ref={playerRef}
          src={mediaUrl || ''}
          onReadySrc={() => setPlayerReady(true)}
          onLoadTimeoutSrc={() =>
            sendAlert('We could not load some of the audio for this lesson.', {
              type: 'warning',
              title: 'There was a problem',
            })
          }
        />
      </>
    );
  },
);

TraceReviewSound.displayName = 'TraceReviewSound';

interface SpellingReviewSoundsStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  sound: string;
}

const SpellingReviewSoundsStep = ({
  activity,
  mode,
  wrapperClassName,
  index,
  total,
  sound,
  isPreview,
}: SpellingReviewSoundsStepProps) => {
  const traceReviewSoundRef = useRef<TraceReviewSound | null>(null);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      usePanel
      title={`Click the button to hear the sound. Write the grapheme.`}
      showTitle={mode === 'TEACH'}
      showActivityPagination={false}
      isPreview={isPreview}
      getActivityResponseMetaData={() => {
        return {
          text: sound,
          base64Image: traceReviewSoundRef.current?.asBitmap(),
          points: JSON.stringify(traceReviewSoundRef.current?.asVector()),
        };
      }}
    >
      <TraceReviewSound ref={traceReviewSoundRef} sound={sound} />
    </ActivityStep>
  );
};

export const SpellingReviewSounds = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  const { getLessonByActivity } = useLessonPlayer();

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const sounds = useMemo(() => {
    if (!activity.sounds) {
      return;
    }

    if (!soundsData) {
      return;
    }

    const lesson = getLessonByActivity(activity);

    const soundsFromString = findSoundsInString(
      soundsData?.getPhonicsSounds.map((sound) => sound.name) || [],
      activity.sounds,
      lesson?.number,
    );

    return getRandomEntries(soundsFromString, MAX_SOUNDS);
  }, [soundsData, activity, getLessonByActivity]);

  return (
    sounds && (
      <Activity
        steps={[
          ...(mode === 'TEACH'
            ? [
                <ActivityTitleStep
                  key={`${activity.id}-title`}
                  activity={activity}
                  mode={mode}
                  wrapperClassName={wrapperClassName}
                  isPreview={isPreview}
                  index={0}
                  total={sounds.length + 1}
                />,
              ]
            : []),
          ...sounds?.flatMap((sound, index) => [
            <SpellingReviewSoundsStep
              key={`${activity.id}-${index}-1`}
              activity={activity}
              mode={mode}
              wrapperClassName={wrapperClassName}
              isPreview={isPreview}
              index={index + 1}
              total={sounds.length + 1}
              sound={sound}
            />,
          ]),
        ]}
      />
    )
  );
};
